import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { createRecordFail, createRecordSuccess, editRecordFail, editRecordSuccess, errorSave, exceedingRecordsHoursMessage, invalidDescriptionMessage, requestLoading, requestMissed, requestPending, requestSuccessful, successSave } from "../../../constants/constants";
import { getProjectTags, getProjectTasks, getRoles, postSaveRecord, postSaveMultipleRecords, updateRecord } from "../../http/httpClient";
import { RecordDTO } from "../../models/recordModelDTO";
import { Tag } from "../../models/tagModel";
import { task } from "../../models/taskModel";
import { fetchWeeklyRecords } from "../dailyTracker/dailyTrackerSlice";
import { fetchMonthlyRecords } from "../monthlyTracker/monthlyTrackerSlice";
import { RootState } from "../store";
import { Role } from "../../models/roleModel";

const namespace = 'saveRecordDialog';
const initialState = {
    status: requestPending,
    isOpen: false,
    tasks: new Array<task>(),
    tasksStatus: requestPending,
    tags: new Array<Tag>(),
    tagsStatus: requestPending,
    roles: new Array<Role>(),
    rolesStatus: requestPending,
    selectedData:{
        id: 0,
        date: new Date(),
        timeQty:'00:00:00',
        ticketId: '',
        description: '',
        TaskId: 0,
        ProjectId: 0,
        StageId: 0,
        ProjectRoleId: 0,
        tagRecords: new Array<{tagId:number}>(),
        timeSelection: new Date(0,0,0,0,0)
    },
};

export const fetchProjectTasks = createAsyncThunk(
    `${namespace}/fetchProjectTasks`,
    async (idProject: number) => await getProjectTasks(idProject,false),
);
export const fetchProjectTags = createAsyncThunk(
    `${namespace}/fetchProjectTags`,
    async (idProject: number) => await getProjectTags(idProject,false),
);
export const fetchRoles = createAsyncThunk(
    `${namespace}/fetchRoles`,
    async () => await getRoles(false),
);
export const updateRecordThunk = createAsyncThunk(
    `${namespace}/updateRecord`,
    async (ResourceId:number,thunkAPI) => {
        const {saveRecordDialog:state} = thunkAPI.getState() as RootState;
        
        const formatDateWithoutTimezone = (date: Date | string) => {
            let dateObj: Date;
            if (date instanceof Date) {
                dateObj = date;
            } else if (typeof date === 'string') {
                dateObj = new Date(date);
            } else {
                dateObj = new Date();
            }
            
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        const formattedDate = formatDateWithoutTimezone(state.selectedData.date);
        
        const recordData = {
            id: state.selectedData.id || 0,
            ResourceId: ResourceId,
            date: formattedDate,
            timeQty: state.selectedData.timeQty,
            timeStamp: new Date(),
            description: state.selectedData.description || '',
            ticketId: state.selectedData.ticketId || '',
            TaskId: state.selectedData.TaskId || 0,
            ProjectId: state.selectedData.ProjectId || 0,
            StageId: state.selectedData.StageId || 0,
            ProjectRoleId: state.selectedData.ProjectRoleId || 0,
            tagRecords: state.selectedData.tagRecords || [],
        }
        
        const boolUpdate = await updateRecord(recordData);
        boolUpdate === successSave ? toast.success(editRecordSuccess) : (boolUpdate === errorSave) ? toast.error(editRecordFail) : toast.error(exceedingRecordsHoursMessage);
        
        thunkAPI.dispatch(cleanSaveRecordDialog())
        thunkAPI.dispatch(fetchWeeklyRecords(ResourceId));
        thunkAPI.dispatch(fetchMonthlyRecords(ResourceId));
        return boolUpdate;
    },
);

export const saveRecord = createAsyncThunk(
    `${namespace}/saveRecord`,
    async (ResourceId:number,thunkAPI) => {
        const {saveRecordDialog:state} = thunkAPI.getState() as RootState;
        
        const formatDateWithoutTimezone = (date: Date | string) => {
            let dateObj: Date;
            if (date instanceof Date) {
                dateObj = date;
            } else if (typeof date === 'string') {
                dateObj = new Date(date);
            } else {
                dateObj = new Date();
            }
            
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        const recordData = {
            ...state.selectedData,
            ResourceId,
            date: formatDateWithoutTimezone(state.selectedData.date),
            timeStamp: new Date(),
            id: 0,
        }
        
        const boolNewRecord = await postSaveRecord(recordData);
        boolNewRecord === successSave ? toast.success(createRecordSuccess) : (boolNewRecord === errorSave) ? toast.error(createRecordFail) : toast.error(exceedingRecordsHoursMessage);
        
        thunkAPI.dispatch(cleanSaveRecordDialog())
        thunkAPI.dispatch(fetchWeeklyRecords(ResourceId));
        thunkAPI.dispatch(fetchMonthlyRecords(ResourceId));
        return boolNewRecord;
    },
);

export const saveMultipleRecords = createAsyncThunk(
    `${namespace}/saveMultipleRecords`,
    async ({ ResourceId, dates }: { ResourceId: number, dates: Date[] }, thunkAPI) => {
        const {saveRecordDialog:state} = thunkAPI.getState() as RootState;
        
        const formatDateWithoutTimezone = (date: Date | string) => {
            let dateObj: Date;
            if (date instanceof Date) {
                dateObj = date;
            } else if (typeof date === 'string') {
                dateObj = new Date(date);
            } else {
                dateObj = new Date();
            }
            
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        const { date, ...baseRecordData } = state.selectedData;
        
        const recordsData = {
            ProjectId: baseRecordData.ProjectId,
            Dates: dates.map(date => formatDateWithoutTimezone(date)),
            TimeQty: baseRecordData.timeQty,
            TimeStamp: new Date(),
            Description: baseRecordData.description,
            TicketId: baseRecordData.ticketId,
            TaskId: baseRecordData.TaskId,
            StageId: baseRecordData.StageId,
            RoleId: baseRecordData.ProjectRoleId,
            ResourceId: ResourceId,
            TagRecords: baseRecordData.tagRecords.map(tag => ({ id: tag.tagId }))
        };
        
        const boolNewRecord = await postSaveMultipleRecords(recordsData);
        boolNewRecord === successSave ? toast.success(`Se crearon ${dates.length} registros exitosamente`) : (boolNewRecord === errorSave) ? toast.error(createRecordFail) : toast.error(exceedingRecordsHoursMessage);
        
        thunkAPI.dispatch(cleanSaveRecordDialog())
        thunkAPI.dispatch(fetchWeeklyRecords(ResourceId));
        thunkAPI.dispatch(fetchMonthlyRecords(ResourceId));
        return boolNewRecord;
    },
);

export const saveRecordDialog = createSlice({
    name: namespace,
    initialState: initialState,
    reducers: {
        openSaveRecordDialog: (state,action) => {
            const initData = initialState.selectedData;
            state.selectedData.TaskId = initData.TaskId;
            state.selectedData.ProjectId = initData.ProjectId;
            state.selectedData.ticketId = initData.ticketId;
            state.selectedData.StageId = initData.StageId;
            state.selectedData.date = action.payload.date;
            state.selectedData.description = initData.description;
            state.selectedData.id = initData.id;
            state.selectedData.tagRecords = initData.tagRecords;
            state.selectedData.timeQty = initData.timeQty;
            state.selectedData.timeSelection = initData.timeSelection;
            if(action.payload.record){
                const record  = action.payload.record as RecordDTO;
                state.selectedData.id = record.id;
                state.selectedData.TaskId = record.taskId;
                state.selectedData.ProjectId = record.projectId;
                state.selectedData.StageId = record.stageId;
                state.selectedData.ticketId = record.ticketId;
                state.selectedData.ProjectRoleId = record.roleId;
                state.selectedData.description = record.description;
                state.selectedData.tagRecords = record.tagRecords.map(tagRecord => { return { tagId: tagRecord.id } });
                state.selectedData.timeQty = record.timeQty.toString();
                const partTimeQty = state.selectedData.timeQty.split(':')
                state.selectedData.timeSelection = new Date(0,0,0,parseInt(partTimeQty[0]),parseInt(partTimeQty[1]))
            };
            state.isOpen = true;
        },
        closeSaveRecordDialog: (state)=> {
            state.isOpen = false;
        },

        cleanSaveRecordDialog: (state)=> {
            state.selectedData.timeSelection = new Date(0,0,0,0,0);
        },

        selectProject: (state, action) => {
            state.selectedData.ProjectId = action.payload;
            state.selectedData.StageId = 0;
            state.selectedData.TaskId = 0;
            state.selectedData.tagRecords = new Array<{tagId:number}>();
        },
        setRole: (state, action) => {
            state.selectedData.ProjectRoleId = action.payload;
        },
        selectStage: (state, action) => {
            state.selectedData.StageId = action.payload;
        },
        selectTask: (state, action) => {
            state.selectedData.TaskId = action.payload;
        },
        selectTags: (state, action) => {
            state.selectedData.tagRecords = action.payload.map((tagId:number) => {return {tagId:tagId}});
        },
        setDescription: (state, action) => {
            if (action.payload.length <= 256){
                state.selectedData.description = action.payload;                
            } else {
                toast.error(invalidDescriptionMessage);
            }
        },
        setTicketId: (state,action) =>{
            state.selectedData.ticketId = action.payload;
        },
        setDate: (state,action) =>{
            state.selectedData.date = action.payload;
        },
        setTimeQty: (state,action) =>{
            if (action.payload instanceof Date ) {
                state.selectedData.timeQty = (action.payload.getHours() < 10? "0" + action.payload.getHours().toString() : + action.payload.getHours().toString()) + ':' + 
                (action.payload.getMinutes() < 10? "0" + action.payload.getMinutes().toString() : action.payload.getMinutes().toString());
                state.selectedData.timeSelection = action.payload;
            } else {
                state.selectedData.timeQty = action.payload;
            }
        },

    },
    extraReducers: (builder) => {
        builder.addCase(fetchProjectTasks.pending, (state) => {
            state.tasksStatus = requestLoading;
        }).addCase(fetchProjectTasks.fulfilled, (state, action) => {
            state.tasks = action.payload;
            state.tasksStatus = requestSuccessful;
        }).addCase(fetchProjectTasks.rejected, (state) => {
            state.tasksStatus = requestMissed;
        });

        builder.addCase(fetchProjectTags.pending, (state) => {
            state.tagsStatus = requestLoading;
        }).addCase(fetchProjectTags.fulfilled, (state, action) => {
            state.tags = action.payload;
            state.tagsStatus = requestSuccessful;
        }).addCase(fetchProjectTags.rejected, (state) => {
            state.tagsStatus = requestMissed;
        });

        builder.addCase(saveRecord.pending, (state) => {
            state.status = requestLoading;
        }).addCase(saveRecord.fulfilled, (state) => {
            state.status = requestSuccessful;
        }).addCase(saveRecord.rejected, (state) => {
            state.status = requestMissed;
        });

        builder.addCase(updateRecordThunk.pending, (state) => {
            state.status = requestLoading;
        }).addCase(updateRecordThunk.fulfilled, (state) => {
            state.status = requestSuccessful;
        }).addCase(updateRecordThunk.rejected, (state) => {
            state.status = requestMissed;
        });

        builder.addCase(fetchRoles.pending, (state) => {
            state.rolesStatus = requestLoading;
        }).addCase(fetchRoles.fulfilled, (state,action) => {
            state.roles = action.payload;
            state.rolesStatus = requestSuccessful;
        }).addCase(fetchRoles.rejected, (state) => {
            state.rolesStatus = requestMissed;
        })
    },
})

export const saveRecordDialogReducer = saveRecordDialog.reducer;
export const {openSaveRecordDialog,closeSaveRecordDialog, selectStage, selectProject, selectTask, selectTags, setDate,
                setTimeQty, setDescription, setTicketId, setRole, cleanSaveRecordDialog } = saveRecordDialog.actions;